import { billKinds, kindMissing } from './bill-kinds';
import { CONTRACT_RESOURCE } from './contract';
import { RESHAPE_RESOURCE } from './reshape';
import { WRAP_RESOURCE } from './wrap';
import { type DeliveryState, destinationOf, platformTopicFault } from './destinations';
import { scopeAddressFault } from './endpoints';
import { flowIssues } from './flow-issues';
import type { IdentityState } from './identity';
import { contentHash, runTests, unmappedRequiredFields } from './transform';
import type { TransformState } from './transforms';
import { drawsMapping } from './xslt';
import {
  CanonicalSchema,
  DirectorySource,
  DocsSource,
  FieldSource,
  OriginalSource,
  Draft,
  FieldMapping,
  FLOW_RESOURCE,
  GroupTemplate,
  GroupTemplateMember,
  PublishBlocker,
  Revision,
  RouteDef,
  RouteGroup,
  Sample,
  Sink,
  Tenant,
  TestRunResult,
  Vendor,
} from './types';

export interface ConsoleState extends DeliveryState, TransformState, IdentityState {
  templates?: GroupTemplate[];
  alerts?: { failures?: { enabled: boolean; webhookUrl?: string } };
  directorySource?: DirectorySource;
  docsSource?: DocsSource;
  /** Where every endpoint's bills go unless its flow sends them somewhere else. */
  defaultDestination?: Sink;
  /** Where Casa's own field names are read from, when they are read at all. */
  fieldSource?: FieldSource;
  /** Where a record of how a bill was delivered is read from, for reversing one. */
  originalSource?: OriginalSource;
  tenantGroups?: { vendorId: string; tenantId: string; groupIds: string[] }[];
  businessGroups?: { vendorId: string; businessId: string; groupIds: string[] }[];
  tenantOwnedGroups?: { vendorId: string; tenantId: string; groupIds: string[] }[];
  vendors: Vendor[];
  tenants: Tenant[];
  groups: RouteGroup[];
  routes: RouteDef[];
  samples: Sample[];
  drafts: Draft[];
  revisions: Revision[];
  runs: TestRunResult[];
  schemas: CanonicalSchema[];
}

const key = (routeId: string, scope: string) => `${routeId}::${scope}`;

/** A new installation has no vendors, contracts, templates or sample data. */
export const emptyState = (): ConsoleState => ({
  templates: [],
  vendors: [],
  tenants: [],
  groups: [],
  routes: [],
  samples: [],
  drafts: [],
  revisions: [],
  runs: [],
  schemas: [],
  businesses: [],
  credentials: [],
  routeBindings: [],
  authSchemes: [],
  vendorRules: [],
  transforms: [],
});

export const templates = (state?: ConsoleState): GroupTemplate[] => state?.templates ?? [];

export const findTemplate = (id: string, state?: ConsoleState): GroupTemplate | undefined =>
  templates(state).find((template) => template.id === id);

export const vendorGroups = (state: ConsoleState, vendorId: string): RouteGroup[] =>
  state.groups.filter((group) => group.vendorId === vendorId);

export const groupRoutes = (state: ConsoleState, groupId: string): RouteDef[] =>
  state.routes.filter((route) => route.groupId === groupId);

/** Authoring fixtures may be inherited; this does not add a runtime mapping fallback. */
/**
 * Whether this scope is one of the route's own, and so runs the vendor's configuration when it
 * has authored none of its own. A tenant qualifies when it is connected to the route's vendor,
 * and a business when its tenant is — a business is never configured for a vendor its tenant
 * does not use.
 */
export const scopeInheritsShared = (state: ConsoleState, routeId: string, scope: string): boolean => {
  const route = state.routes.find((item) => item.id === routeId);
  const tenantIds = state.vendors.find((item) => item.id === route?.vendorId)?.tenantIds ?? [];
  if (scope.startsWith('tenant:')) return tenantIds.includes(scope.slice(7));
  if (scope.startsWith('business:')) {
    const business = (state.businesses ?? []).find((item) => item.id === scope.slice(9));

    return Boolean(business && tenantIds.includes(business.tenantId));
  }

  return false;
};

export const routeSamples = (state: ConsoleState, routeId: string, scope: string): Sample[] => {
  const own = state.samples.filter((sample) => sample.routeId === routeId && sample.scope === scope);
  return own.length || !scopeInheritsShared(state, routeId, scope)
    ? own
    : state.samples.filter((sample) => sample.routeId === routeId && sample.scope === 'shared');
};

export const findDraft = (state: ConsoleState, routeId: string, scope: string): Draft | undefined =>
  state.drafts.find((draft) => draft.routeId === routeId && draft.scope === scope);

export const findRun = (state: ConsoleState, routeId: string, scope: string): TestRunResult | undefined =>
  state.runs.find((run) => run.routeId === routeId && run.scope === scope);

export const routeRevisions = (state: ConsoleState, routeId: string, scope: string): Revision[] =>
  state.revisions
    .filter((revision) => revision.routeId === routeId && revision.scope === scope)
    .sort((a, b) => b.version - a.version);

export const liveRevision = (state: ConsoleState, routeId: string, scope: string): Revision | undefined =>
  routeRevisions(state, routeId, scope).find((revision) => revision.live);

export const findSchema = (state: ConsoleState, id?: string): CanonicalSchema | undefined =>
  state.schemas.find((schema) => schema.id === id);

export const vendorReadiness = (state: ConsoleState, vendorId: string) => {
  const routes = state.routes.filter((route) => route.vendorId === vendorId);
  const live = routes.filter((route) => liveRevision(state, route.id, 'shared') !== undefined).length;

  return { total: routes.length, configured: routes.filter((route) => route.configured).length, live };
};

export const publishBlockers = (state: ConsoleState, routeId: string, scope: string): PublishBlocker[] => {
  const route = state.routes.find((candidate) => candidate.id === routeId);
  const draft = findDraft(state, routeId, scope);
  const samples = routeSamples(state, routeId, scope);
  const run = findRun(state, routeId, scope);
  const schema = findSchema(state, route?.canonicalSchemaId);
  const blockers: PublishBlocker[] = [];

  if (samples.length === 0) blockers.push({ code: 'no-samples', message: 'Add at least one vendor sample.' });
  if (!schema) blockers.push({ code: 'no-schema', message: 'Attach a canonical schema.' });
  if (!destinationOf(state, route, draft?.resources?.[FLOW_RESOURCE]))
    blockers.push({
      code: 'no-sink',
      message: "Casa's bill topic is not set yet. Set it under Settings on Live sync.",
    });

  /*
   * A pass-through endpoint needs no mapping: a scope without one hands the payload on as it came.
   * A scope that draws its own — one tenant asking for a field of theirs — is held to it like any other.
   */
  const ownMapping = Boolean(draft && drawsMapping(draft.xslt, draft.mappings));
  if (!route?.passThrough || ownMapping) {
    if (!draft || (!draft.xslt && draft.mappings.filter((mapping) => mapping.targetPath).length === 0)) {
      blockers.push({ code: 'empty-mapping', message: 'Map at least one field.' });
    } else if (!draft.xslt) {
      const missing = unmappedRequiredFields(draft.mappings, schema);
      if (missing.length > 0) {
        blockers.push({ code: 'empty-mapping', message: `Required fields not mapped: ${missing.join(', ')}` });
      }
    }
  }
  /*
   * A flow that was drawn is what actually runs, so it is checked here rather than discovered by
   * a replica. The plane checks again on activation and keeps the previous version serving if it
   * refuses, so this is about telling the author now instead of leaving a publish that looked
   * fine and changed nothing.
   */
  for (const issue of flowIssues(draft?.resources?.[FLOW_RESOURCE] ?? ''))
    blockers.push({ code: 'flow-unrunnable', message: issue });
  /* Every kind of bill the docs show is configured before any of them goes live. */
  const { field, split, kinds } = billKinds(state, routeId, scope);
  if (kinds.length && !split)
    blockers.push({
      code: 'kinds-unsplit',
      message: `Bills of ${kinds.length} kinds arrive here (${field}: ${kinds.map((kind) => kind.value).join(', ')}). Split them by ${field} on the DataMapper step.`,
    });
  else
    for (const kind of kinds) {
      const missing = kindMissing(kind);
      if (missing) blockers.push({ code: 'kind-incomplete', message: missing });
    }
  if (route && !route.ingress?.path?.trim())
    blockers.push({
      code: 'no-address',
      message: "Casa's API docs give this endpoint no address. Add it to the docs, then Sync from docs.",
    });
  const addressFault = route && scopeAddressFault(state, route, scope, draft?.resources?.[FLOW_RESOURCE]);
  if (addressFault) blockers.push({ code: 'address-locked', message: addressFault });
  if (route?.signInInAddress)
    blockers.push({
      code: 'sign-in-in-address',
      message:
        'The vendor puts its key in the address of this endpoint, and Casa checks keys only in a header. Ask a developer how it should sign in.',
    });

  if (draft && (!run || run.revisionId !== draft.revisionId)) {
    blockers.push({ code: 'tests-not-run', message: 'Run tests against the current draft.' });
  } else if (run && run.passed < run.total) {
    blockers.push({ code: 'tests-failing', message: `${run.total - run.passed} of ${run.total} samples fail.` });
  }

  return blockers;
};

/**
 * Where a route's Camel definition lives. Undefined means the group's template ships no flow
 * for that member yet, which is worth saying rather than papering over with a guessed path.
 */
export const flowPathOf = (state: ConsoleState, route: RouteDef): string | undefined => {
  const group = state.groups.find((candidate) => candidate.id === route.groupId);
  const template = templates(state).find((candidate) => candidate.id === group?.templateId);
  const member = template?.members.find((candidate) => candidate.name === route.name);

  return template?.flowDir && member ? `flows/${template.flowDir}/${member.slug}.camel.yaml` : undefined;
};

/**
 * Where a route's vendor-side JSON schema lives, by the same rule as the flow above.
 *
 * Only the control plane read this until now; it is here so both halves name the file the same
 * way, which is the whole reason this package exists.
 */
export const vendorSchemaPathOf = (state: ConsoleState, route: RouteDef): string | undefined => {
  const group = state.groups.find((candidate) => candidate.id === route.groupId);
  const template = findTemplate(group?.templateId ?? '', state);
  const member = template?.members.find((candidate) => candidate.name === route.name);

  return template?.flowDir && member ? `schemas/vendor/${template.flowDir}-${member.slug}.json` : undefined;
};

/**
 * How the data plane names this route. It derives the key from the URL a vendor posts to, so
 * this must match `routeKeyOf` in the control plane exactly — a different slug means the plane
 * finds no configuration and answers 503.
 */
const slug = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export const routeKeyOf = (state: ConsoleState, route: RouteDef): string => {
  const group = state.groups.find((candidate) => candidate.id === route.groupId);

  return `${state.vendors.find((vendor) => vendor.id === route.vendorId)?.slug ?? route.vendorId}/${slug(group?.name ?? 'routes')}/${slug(route.name)}`;
};

/**
 * The URL a vendor actually posts to. The data plane serves one ingress shape for every route
 * — `/ingest/{tenant}/{vendor}/{group}/{route}` — so this, not the path recorded against the
 * route, is what a vendor must be given.
 */
export const planeIngressUrl = (base: string, state: ConsoleState, route: RouteDef, tenantId: string): string =>
  `${base.replace(/\/+$/, '')}/ingest/${encodeURIComponent(tenantId)}/${routeKeyOf(state, route)}`;

export const blastRadius = (state: ConsoleState, vendorId: string, scope: string): number => {
  const vendor = state.vendors.find((candidate) => candidate.id === vendorId);
  if (!vendor) return 0;
  if (scope === 'shared') return vendor.tenantIds.length;

  return 1;
};

let revisionCounter = 0;

export const nextRevisionId = (): string => {
  revisionCounter += 1;
  return `draft-${Date.now().toString(36)}-${revisionCounter.toString(36)}`;
};

export const saveDraft = (
  state: ConsoleState,
  routeId: string,
  scope: string,
  mappings: FieldMapping[],
  authoring?: Pick<Draft, 'xslt' | 'mapperMetadata' | 'resources'>,
): ConsoleState => {
  const draft: Draft = {
    routeId,
    scope,
    mappings,
    ...authoring,
    revisionId: nextRevisionId(),
    updatedAt: new Date().toISOString(),
  };

  return {
    ...state,
    drafts: [
      ...state.drafts.filter((existing) => key(existing.routeId, existing.scope) !== key(routeId, scope)),
      draft,
    ],
  };
};

/**
 * Drops a scope's own mapping, which is how a tenant override is removed: with no draft of its
 * own the scope falls back to reading the shared one.
 */
export const removeDraft = (state: ConsoleState, routeId: string, scope: string): ConsoleState => ({
  ...state,
  drafts: state.drafts.filter((existing) => !(existing.routeId === routeId && existing.scope === scope)),
  runs: state.runs.filter((run) => !(run.routeId === routeId && run.scope === scope)),
});

/**
 * Everything that hangs off a set of routes. A route's mapping, its test run, its published
 * revisions and its captured payloads have no meaning without it, so removing a route means
 * removing those too — otherwise they accumulate as unreachable rows that still answer
 * lookups keyed by route id.
 */
const withoutRoutes = (state: ConsoleState, routeIds: string[]): ConsoleState => {
  const gone = new Set(routeIds);

  return {
    ...state,
    routes: state.routes.filter((route) => !gone.has(route.id)),
    tenantDestinations: state.tenantDestinations?.filter((item) => !gone.has(item.routeId)),
    tenantIngress: state.tenantIngress?.filter((item) => !gone.has(item.routeId)),
    deliveryRevisions: state.deliveryRevisions?.filter((item) => !gone.has(item.routeId)),
    samples: state.samples.filter((sample) => !gone.has(sample.routeId)),
    drafts: state.drafts.filter((draft) => !gone.has(draft.routeId)),
    revisions: state.revisions.filter((revision) => !gone.has(revision.routeId)),
    runs: state.runs.filter((run) => !gone.has(run.routeId)),
  };
};

/** Removes one route, and forgets it in the group that listed it. */
export const removeRoute = (state: ConsoleState, routeId: string): ConsoleState => {
  const next = withoutRoutes(state, [routeId]);

  return {
    ...next,
    groups: next.groups.map((group) =>
      group.routeIds.includes(routeId) ? { ...group, routeIds: group.routeIds.filter((id) => id !== routeId) } : group,
    ),
  };
};

export const recordRun = (state: ConsoleState, run: TestRunResult): ConsoleState => ({
  ...state,
  runs: [
    ...state.runs.filter((existing) => key(existing.routeId, existing.scope) !== key(run.routeId, run.scope)),
    run,
  ],
});

export const executeTests = (state: ConsoleState, routeId: string, scope: string): TestRunResult | undefined => {
  const draft = findDraft(state, routeId, scope);
  const route = state.routes.find((candidate) => candidate.id === routeId);
  if (!draft || !route) return undefined;

  return runTests(
    routeId,
    scope,
    draft.revisionId,
    routeSamples(state, routeId, scope),
    draft.mappings,
    findSchema(state, route.canonicalSchemaId),
  );
};

export const publish = (state: ConsoleState, routeId: string, scope: string, publishedBy: string): ConsoleState => {
  const draft = findDraft(state, routeId, scope);
  if (!draft) return state;

  const version =
    routeRevisions(state, routeId, scope).reduce((max, revision) => Math.max(max, revision.version), 0) + 1;
  const revision: Revision = {
    version,
    routeId,
    scope,
    publishedAt: new Date().toISOString(),
    publishedBy,
    /**
     * The flow is part of what a revision publishes, so it is part of what identifies it. A
     * hash over the mappings alone would be unchanged by a flow-only edit, and the runtime —
     * which skips a pointer whose hash it already runs — would keep serving the old flow.
     */
    contentHash: contentHash(
      draft.mappings,
      [
        draft.resources?.[FLOW_RESOURCE],
        draft.resources?.[CONTRACT_RESOURCE],
        draft.resources?.[RESHAPE_RESOURCE],
        draft.resources?.[WRAP_RESOURCE],
      ]
        .filter(Boolean)
        .join('\u0000'),
    ),
    live: true,
    mappings: draft.mappings,
    flow: draft.resources?.[FLOW_RESOURCE],
    /**
     * The rules the vendor's payload is held to, pinned like the flow. A revision that runs
     * without them would accept what its author had already refused, and a rollback would take
     * the mapping back while leaving the rules where they were.
     */
    contract: draft.resources?.[CONTRACT_RESOURCE],
    /** How this revision reshapes a document, pinned for the same reason. */
    reshape: draft.resources?.[RESHAPE_RESOURCE],
    wrap: draft.resources?.[WRAP_RESOURCE],
  };

  return {
    ...state,
    revisions: [
      ...state.revisions.map((existing) =>
        key(existing.routeId, existing.scope) === key(routeId, scope) ? { ...existing, live: false } : existing,
      ),
      revision,
    ],
    routes: state.routes.map((route) => (route.id === routeId ? { ...route, configured: true } : route)),
  };
};

export const rollback = (state: ConsoleState, routeId: string, scope: string, version: number): ConsoleState => ({
  ...state,
  deliveryRevisions: state.deliveryRevisions?.map((item) =>
    item.routeId === routeId && scope === 'shared'
      ? {
          ...item,
          live:
            item.version ===
            [...(state.deliveryRevisions ?? [])]
              .reverse()
              .find((candidate) => candidate.routeId === routeId && candidate.transformationVersion === version)
              ?.version,
        }
      : item,
  ),
  revisions: state.revisions.map((revision) =>
    key(revision.routeId, revision.scope) === key(routeId, scope)
      ? { ...revision, live: revision.version === version }
      : revision,
  ),
});

/**
 * Stop serving a scope, without forgetting it was ever served.
 *
 * A tenant's own rules are published for a reason and withdrawn for one too — they were an
 * experiment, or that tenant stopped needing them. What must not happen is the only way back
 * being to publish something else on top: that leaves a revision live that nobody wants and
 * nothing to say so.
 *
 * The revisions stay for the audit trail. What stops is the serving: no revision of this scope is
 * live, so it is no longer advertised, and callers who resolved to it fall back to the scope
 * above — which is what they had before it existed.
 */
export const withdrawScope = (state: ConsoleState, routeId: string, scope: string): ConsoleState => {
  if (scope === 'shared') return state;

  return {
    ...state,
    revisions: state.revisions.map((revision) =>
      key(revision.routeId, revision.scope) === key(routeId, scope) ? { ...revision, live: false } : revision,
    ),
  };
};

export const addVendor = (state: ConsoleState, name: string, templateIds: string[]): ConsoleState => {
  const id = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  if (
    !id ||
    state.vendors.some(
      (vendor) => vendor.id === id || vendor.slug === id || vendor.name.toLowerCase() === name.trim().toLowerCase(),
    )
  )
    return state;

  const made: ReturnType<typeof groupFromTemplate>[] = [];
  templateIds.forEach((templateId) => {
    const template = findTemplate(templateId, state);
    if (!template || made.some(({ group }) => slug(group.name) === slug(template.name))) return;
    made.push(groupFromTemplate(template, id));
  });

  return {
    ...state,
    vendors: [...state.vendors, { id, name: name.trim(), tenantIds: [] }],
    groups: [...state.groups, ...made.map(({ group }) => group)],
    routes: [...state.routes, ...made.flatMap(({ routes }) => routes)],
    samples: [...state.samples, ...made.flatMap(({ samples }) => samples)],
  };
};

/**
 * Moves a vendor one place in the list. Ordering is an authoring preference — the plane reads
 * routes by key, so nothing about it reaches the runtime or a published artefact.
 */
export const reorderVendor = (state: ConsoleState, vendorId: string, direction: 'up' | 'down'): ConsoleState => {
  const from = state.vendors.findIndex((vendor) => vendor.id === vendorId);
  const to = direction === 'up' ? from - 1 : from + 1;
  if (from === -1 || to < 0 || to >= state.vendors.length) return state;

  const vendors = [...state.vendors];
  [vendors[from], vendors[to]] = [vendors[to], vendors[from]];

  return { ...state, vendors };
};

/**
 * A group of endpoints that came from no template.
 *
 * Templates are a convenience, not a requirement: a vendor whose API resembles nothing on the
 * shelf still has to be configurable, so a group can be created empty and filled endpoint by
 * endpoint.
 */
export const addGroup = (state: ConsoleState, vendorId: string, name: string): ConsoleState => {
  const trimmed = name.trim();
  const slugged = slug(trimmed);
  if (!state.vendors.some((vendor) => vendor.id === vendorId) || !slugged) return state;
  const id = `${vendorId}-${slugged}`;
  const taken = state.groups.some(
    (group) => group.id === id || (group.vendorId === vendorId && slug(group.name) === slugged),
  );
  if (taken) return state;

  return { ...state, groups: [...state.groups, { id, templateId: '', vendorId, name: trimmed, routeIds: [] }] };
};

/**
 * One template made into one vendor's group. Creating a vendor from a template and attaching the
 * template later both come here, so the same group never means two things depending on which
 * button was pressed.
 *
 * A member carries more than a name: the address its vendors are told to call, how they sign in,
 * whether a request holds a list, and a real payload to test against — kept as the route's first
 * sample, so a mapping can be tested the moment the group is added.
 */
/** Who answers a GET from the docs until someone names another service on the endpoint. */
export const DEFAULT_UPSTREAM = 'rule-server';

/** An endpoint's examples from Casa's API docs, each a sample named as the docs name it. */
export const docsSamples = (routeId: string, member: GroupTemplateMember): Sample[] =>
  (
    member.samples ??
    (member.sample ? [{ name: `${member.name} — from the documentation`, payload: member.sample }] : [])
  ).map((example) => ({
    id: `sample-${routeId}-docs-${slug(example.name)}`,
    routeId,
    scope: 'shared',
    name: example.name,
    payload: example.payload,
    source: 'docs' as const,
  }));

const groupFromTemplate = (
  template: GroupTemplate,
  vendorId: string,
): { group: RouteGroup; routes: RouteDef[]; samples: Sample[] } => {
  const groupId = `${vendorId}-${template.id}`;
  const group: RouteGroup = {
    id: groupId,
    templateId: template.id,
    vendorId,
    name: template.name,
    routeIds: [],
    ...(template.authSchemeId ? { authSchemeId: template.authSchemeId } : {}),
  };
  const routes = template.members.map((member) => {
    const routeId = `${groupId}-${member.slug}`;
    group.routeIds.push(routeId);
    const asks = member.method === 'GET';
    const address = member.path ? { method: member.method, path: member.path } : {};
    const ingress = { ...address, ...(member.authSchemeId ? { authSchemeId: member.authSchemeId } : {}) };
    return {
      id: routeId,
      name: member.name,
      groupId,
      vendorId,
      canonicalSchemaId: member.canonicalSchemaId,
      sink: member.sink,
      ...(Object.keys(ingress).length ? { ingress } : {}),
      ...(member.isBatch ? { isBatch: true } : {}),
      ...(member.requiredFields?.length ? { requiredFields: member.requiredFields } : {}),
      ...(member.signInInAddress ? { signInInAddress: true } : {}),
      ...(asks ? { upstream: DEFAULT_UPSTREAM } : {}),
      configured: false,
    } satisfies RouteDef;
  });
  const samples = template.members.flatMap((member) => docsSamples(`${groupId}-${member.slug}`, member));
  const canaries = new Map(samples.map((sample): [string, string] => [sample.routeId, sample.id]).reverse());
  routes.forEach((route) => {
    const first = canaries.get(route.id);
    if (first) Object.assign(route, { canarySampleId: first });
  });

  return { group, routes, samples };
};

export const attachGroup = (state: ConsoleState, vendorId: string, templateId: string): ConsoleState => {
  const template = findTemplate(templateId, state);
  if (!template) return state;
  const alreadyAttached = state.groups.some(
    (group) =>
      group.id === `${vendorId}-${template.id}` ||
      (group.vendorId === vendorId && (group.templateId === templateId || slug(group.name) === slug(template.name))),
  );
  if (alreadyAttached) return state;

  const { group, routes, samples } = groupFromTemplate(template, vendorId);

  return {
    ...state,
    groups: [...state.groups, group],
    routes: [...state.routes, ...routes],
    samples: [...state.samples, ...samples],
  };
};

/**
 * Removes a route group and everything that belonged to its endpoints.
 *
 * Dropping the routes alone used to leave their revisions behind, and a revision is what the
 * runtime is told to serve — so a detached group kept running with nothing left in the console
 * to show it.
 */
export const detachGroup = (state: ConsoleState, groupId: string): ConsoleState => {
  const routeIds = state.routes.filter((route) => route.groupId === groupId).map((route) => route.id);

  return {
    ...withoutRoutes(state, routeIds),
    groups: state.groups.filter((group) => group.id !== groupId),
  };
};

/**
 * Everything configured for a vendor, gone: its route groups with their endpoints, drafts and
 * revisions, and which of them its shops use. The vendor and its shops stay, because Casa lists them.
 */
export const clearVendorSetup = (state: ConsoleState, vendorId: string): ConsoleState => {
  const cleared = state.groups
    .filter((group) => group.vendorId === vendorId)
    .reduce((current, group) => detachGroup(current, group.id), state);
  const others = <T extends { vendorId: string }>(items?: T[]) => items?.filter((item) => item.vendorId !== vendorId);

  return {
    ...cleared,
    tenantGroups: others(cleared.tenantGroups),
    businessGroups: others(cleared.businessGroups),
    tenantOwnedGroups: others(cleared.tenantOwnedGroups),
  };
};

export const updateRoute = (state: ConsoleState, routeId: string, patch: Partial<RouteDef>): ConsoleState => ({
  ...state,
  routes: state.routes.map((route) => (route.id === routeId ? { ...route, ...patch } : route)),
});

/**
 * Point a route at a different canonical contract.
 *
 * What was verified against the old contract says nothing about the new one, so the recorded
 * run goes with it. This holds for any contract; nothing here knows what the fields mean.
 */
export const setRouteSchema = (state: ConsoleState, routeId: string, canonicalSchemaId: string): ConsoleState => {
  const route = state.routes.find((candidate) => candidate.id === routeId);
  const next = updateRoute(state, routeId, { canonicalSchemaId });

  return !route || route.canonicalSchemaId === canonicalSchemaId
    ? next
    : { ...next, runs: next.runs.filter((run) => run.routeId !== routeId) };
};

export const setRouteSink = (state: ConsoleState, routeId: string, sink: Sink): ConsoleState => {
  const fault = platformTopicFault(sink);
  if (fault) throw new Error(fault);
  return updateRoute(state, routeId, { sink });
};

export const addSample = (state: ConsoleState, sample: Sample): ConsoleState => ({
  ...state,
  samples: [...state.samples, sample],
});

export const removeSample = (state: ConsoleState, sampleId: string): ConsoleState => ({
  ...state,
  samples: state.samples.filter((sample) => sample.id !== sampleId),
});

export const addTenant = (state: ConsoleState, name: string, externalId?: string): ConsoleState => {
  /*
   * An id supplied by the directory is kept exactly as it came. Inventing one from the name is
   * only what happens when there is no directory to ask, and it is the thing that would leave a
   * tenant with two identifiers — one here and one wherever it really lives.
   */
  const id =
    externalId?.trim() ||
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  if (!id || state.tenants.some((tenant) => tenant.id === id)) return state;

  return { ...state, tenants: [...state.tenants, { id, name: name.trim(), vendorIds: [] }] };
};

/**
 * A branch that has no route groups of this vendor yet is given all the ones its shop holds.
 *
 * Without an entry a branch is refused everything, which is right for a branch somebody narrowed
 * on purpose and wrong for one that simply arrived: its key would be turned away on the first
 * bill. So an entry that exists is left exactly as it is, and only a missing one is written.
 *
 * `repair` is for the one-press fix a person asks for: a shop list that holds none of the vendor's
 * groups is given all of them, and a branch left with nothing gets everything its shop holds.
 * A list that already grants something is never touched.
 */
export const grantVendorGroups = (
  state: ConsoleState,
  vendorId: string,
  tenantId?: string,
  repair = false,
): ConsoleState => {
  const vendor = state.vendors.find((item) => item.id === vendorId);
  if (!vendor) return state;
  const all = vendorGroups(state, vendorId).map((group) => group.id);
  const inScope = (forTenant: string) => vendor.tenantIds.includes(forTenant) && (!tenantId || forTenant === tenantId);

  const tenantGroups = repair
    ? state.tenantGroups?.map((item) =>
        item.vendorId === vendorId && inScope(item.tenantId) && !item.groupIds.some((id) => all.includes(id))
          ? { ...item, groupIds: all }
          : item,
      )
    : state.tenantGroups;
  const held = (forTenant: string) => {
    const selected = tenantGroups?.find((item) => item.vendorId === vendorId && item.tenantId === forTenant);
    return all.filter((id) => !selected || selected.groupIds.includes(id));
  };

  const businesses = (state.businesses ?? []).filter((business) => inScope(business.tenantId));
  const entryOf = (businessId: string) =>
    state.businessGroups?.find((item) => item.vendorId === vendorId && item.businessId === businessId);
  const empty = (businessId: string, tenant: string) =>
    !entryOf(businessId)?.groupIds.some((id) => held(tenant).includes(id));

  const repaired = repair
    ? state.businessGroups?.map((item) => {
        const business = businesses.find((candidate) => candidate.id === item.businessId);
        return item.vendorId === vendorId && business && empty(business.id, business.tenantId)
          ? { ...item, groupIds: held(business.tenantId) }
          : item;
      })
    : state.businessGroups;
  const granted = businesses
    .filter((business) => !entryOf(business.id))
    .map((business) => ({ vendorId, businessId: business.id, groupIds: held(business.tenantId) }))
    .filter((entry) => entry.groupIds.length > 0);

  if (!repair && granted.length === 0) return state;
  return {
    ...state,
    ...(tenantGroups ? { tenantGroups } : {}),
    businessGroups: [...(repaired ?? []), ...granted],
  };
};

/** A group added to a vendor after its shops were chosen, given to every shop and branch of them. */
export const shareGroupWithShops = (state: ConsoleState, groupId: string): ConsoleState => {
  const group = state.groups.find((item) => item.id === groupId);
  if (!group) return state;
  const add = <T extends { vendorId: string; groupIds: string[] }>(item: T): T =>
    item.vendorId === group.vendorId && !item.groupIds.includes(groupId)
      ? { ...item, groupIds: [...item.groupIds, groupId] }
      : item;
  const shared = {
    ...state,
    tenantGroups: state.tenantGroups?.map(add),
    businessGroups: state.businessGroups?.map(add),
  };
  return grantVendorGroups(shared, group.vendorId);
};

/** Several route groups added at once, each optionally given to every shop, or none of them added. */
export const attachGroups = (
  state: ConsoleState,
  vendorId: string,
  templateIds: string[],
  forAllShops: boolean,
): ConsoleState => {
  if (templateIds.length === 0) throw new Error('Choose at least one route group.');
  return [...new Set(templateIds)].reduce((current, templateId) => {
    const next = attachGroup(current, vendorId, templateId);
    const added = next.groups.at(-1);
    if (next.groups.length === current.groups.length || !added)
      throw new Error(`Route group ${templateId} is unknown or already added to this vendor.`);
    return forAllShops ? shareGroupWithShops(next, added.id) : next;
  }, state);
};

export const joinTenant = (state: ConsoleState, tenantId: string, vendorId: string): ConsoleState => ({
  ...state,
  tenants: state.tenants.map((tenant) =>
    tenant.id === tenantId && !tenant.vendorIds.includes(vendorId)
      ? { ...tenant, vendorIds: [...tenant.vendorIds, vendorId] }
      : tenant,
  ),
  vendors: state.vendors.map((vendor) =>
    vendor.id === vendorId && !vendor.tenantIds.includes(tenantId)
      ? { ...vendor, tenantIds: [...vendor.tenantIds, tenantId] }
      : vendor,
  ),
});

export const connectTenant = (state: ConsoleState, tenantId: string, vendorId: string): ConsoleState =>
  grantVendorGroups(joinTenant(state, tenantId, vendorId), vendorId, tenantId);

export const disconnectTenant = (state: ConsoleState, tenantId: string, vendorId: string): ConsoleState => ({
  ...state,
  tenantIngress: state.tenantIngress?.filter(
    (item) =>
      !(
        item.tenantId === tenantId &&
        state.routes.some((route) => route.id === item.routeId && route.vendorId === vendorId)
      ),
  ),
  tenantDestinations: state.tenantDestinations?.filter(
    (item) =>
      !(
        item.tenantId === tenantId &&
        state.routes.some((route) => route.id === item.routeId && route.vendorId === vendorId)
      ),
  ),
  drafts: state.drafts.filter(
    (item) =>
      !(
        item.scope === `tenant:${tenantId}` &&
        state.routes.some((route) => route.id === item.routeId && route.vendorId === vendorId)
      ),
  ),
  runs: state.runs.filter(
    (item) =>
      !(
        item.scope === `tenant:${tenantId}` &&
        state.routes.some((route) => route.id === item.routeId && route.vendorId === vendorId)
      ),
  ),
  samples: state.samples.filter(
    (item) =>
      !(
        item.scope === `tenant:${tenantId}` &&
        state.routes.some((route) => route.id === item.routeId && route.vendorId === vendorId)
      ),
  ),
  tenants: state.tenants.map((tenant) =>
    tenant.id === tenantId ? { ...tenant, vendorIds: tenant.vendorIds.filter((id) => id !== vendorId) } : tenant,
  ),
  vendors: state.vendors.map((vendor) =>
    vendor.id === vendorId ? { ...vendor, tenantIds: vendor.tenantIds.filter((id) => id !== tenantId) } : vendor,
  ),
});

/** How many captured samples a route keeps before the oldest is dropped. */
export const CAPTURE_LIMIT = 20;

const shapeOf = (value: unknown, depth = 0): string => {
  if (depth > 6) return '…';
  if (value === null) return 'null';
  if (Array.isArray(value)) return `[${value.length === 0 ? '' : shapeOf(value[0], depth + 1)}]`;
  if (typeof value === 'object') {
    return `{${Object.keys(value as object)
      .sort()
      .map((key) => `${key}:${shapeOf((value as Record<string, unknown>)[key], depth + 1)}`)
      .join(',')}}`;
  }

  return typeof value;
};

/**
 * A payload the vendor sent, kept as a sample.
 *
 * Two payloads of the same shape are the same test, so a repeat increments a
 * counter instead of adding another. Captures are capped per route and scope,
 * because a vendor pointed at the capture URL will not stop on its own.
 */
export const captureSample = (
  state: ConsoleState,
  routeId: string,
  scope: string,
  payload: string,
  at = new Date().toISOString(),
): { state: ConsoleState; sample: Sample; repeated: boolean } | undefined => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(payload);
  } catch {
    return undefined;
  }
  if (!state.routes.some((route) => route.id === routeId)) return undefined;

  const shape = shapeOf(parsed);
  const existing = state.samples.find(
    (sample) =>
      sample.routeId === routeId &&
      sample.scope === scope &&
      sample.source === 'captured' &&
      shapeOf(JSON.parse(sample.payload)) === shape,
  );

  if (existing) {
    const updated: Sample = { ...existing, seenCount: (existing.seenCount ?? 1) + 1, capturedAt: at };

    return {
      state: { ...state, samples: state.samples.map((sample) => (sample.id === existing.id ? updated : sample)) },
      sample: updated,
      repeated: true,
    };
  }

  const captured = state.samples.filter(
    (sample) => sample.routeId === routeId && sample.scope === scope && sample.source === 'captured',
  );
  const surplus = captured
    .slice()
    .sort((a, b) => (a.capturedAt ?? '').localeCompare(b.capturedAt ?? ''))
    .slice(0, Math.max(0, captured.length + 1 - CAPTURE_LIMIT))
    .map((sample) => sample.id);

  const sample: Sample = {
    id: `capture-${routeId}-${at}-${captured.length}`,
    routeId,
    scope,
    name: `Captured ${at.slice(0, 19).replace('T', ' ')}`,
    payload,
    source: 'captured',
    capturedAt: at,
    seenCount: 1,
  };

  return {
    state: { ...state, samples: [...state.samples.filter((entry) => !surplus.includes(entry.id)), sample] },
    sample,
    repeated: false,
  };
};

/** The URL a vendor is pointed at while its payload shapes are being collected. */
export const captureUrl = (base: string, routeId: string, scope: string): string =>
  `${base.replace(/\/+$/, '')}/api/v1/capture/${encodeURIComponent(routeId)}/${encodeURIComponent(scope)}`;
